import { Spacing } from '@persona-id/design-tokens';
import { groupBy, isEmpty } from 'lodash/fp';
import { useState } from 'react';
import { Link, Navigate, Route } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import Accordion from 'dashboard/components/Accordion';
import ContextMenu from 'dashboard/components/ContextMenu';
import Permissioned from 'dashboard/components/Permissioned';
import IgnoreHitsModal from 'dashboard/components/ReportResult/IgnoreHitsModal';
import { MatchIconPositive } from 'dashboard/components/ReportResult/MatchIcons';
import ReportResultLayout from 'dashboard/components/ReportResult/ReportResultLayout';
import WatchlistAccordions from 'dashboard/components/ReportResult/WatchlistAccordions';
import WatchlistEntityMatchDetails from 'dashboard/components/ReportResult/WatchlistEntityMatchDetails';
import WatchlistEntityMatchSummary from 'dashboard/components/ReportResult/WatchlistEntityMatchSummary';
import { SentryRoutes } from 'dashboard/components/SentryRoute/SentryRoute';
import {
  ReportWatchlist,
  translateWatchlistBirthdatesForDisplay,
  translateWatchlistMatchType,
  translateWatchlistType,
  WatchlistMatch,
  WatchlistMatchEntity,
  WatchlistType,
} from 'dashboard/models/report/watchlist';
import { ReportSnapshotWatchlist } from 'dashboard/models/report-snapshot';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import Sticker from 'design-system/components/Sticker';
import TabBar, { Tab } from 'design-system/components/TabBar';
import { Paragraph } from 'design-system/components/Text';
import { FontWeight } from 'design-system/variables/text';
import useToggle from 'lib/hooks/useToggle';
import { ApiModel } from 'lib/interfaces/api';
import { Permission } from 'lib/permissions';

export const CATEGORY_ORDER = [
  WatchlistType.Pep,
  WatchlistType.Sanction,
  WatchlistType.FitnessProbity,
  WatchlistType.Warning,
];

const TitleContainer = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
`;

const TabText = styled.div`
  font-weight: ${FontWeight.Medium};
  & > span {
    margin-right: ${Spacing[2]};
  }
`;

const EntityMatchLink = styled(Link)`
  &:not(:last-of-type) > div {
    border-bottom: 1px solid ${({ theme }) => theme.color.border.primary};
  }
`;

const OtherNameText = styled(Paragraph).attrs(() => ({ size: 'sm' }))`
  margin-left: ${Spacing[5]};
`;

interface WatchlistMatchedEntitiesProps {
  entities?: WatchlistMatchEntity[];
}

export const LegacyWatchlistMatchedEntities = ({ entities }: WatchlistMatchedEntitiesProps) => {
  return (
    <>
      {entities?.map((entity, index) => {
        return (
          <div key={index}>
            {[
              `${entity['alias-type']} match`,
              <bdi key={index}>{entity['name-raw'] || entity.name}</bdi>,
              entity.title,
              entity.birthdate && `DOB: ${entity.birthdate}`,
            ]
              .filter((item) => item)
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              .reduce((prev, curr) => (prev.length === 0 ? [curr] : [...prev, ' • ', curr]), [])}
          </div>
        );
      })}
    </>
  );
};

export const PersonaWatchlistMatchedEntities = ({ entities }: WatchlistMatchedEntitiesProps) => {
  const hasGroupID = entities && entities.length > 0 && entities[0]['group-id'];
  if (hasGroupID) {
    const listMatchedEntities = groupBy('group-id', entities);
    return (
      <>
        {Object.values(listMatchedEntities).map((group, index) => {
          const mainName = group.shift();
          return (
            <Accordion
              key={index}
              titleGapSize='0px'
              defaultExpanded={false}
              caretSize='sm'
              disabled={group.length === 0}
              title={
                <Paragraph size='xs'>
                  {[
                    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                    translateWatchlistMatchType(mainName['alias-type']),
                    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                    <bdi key={index}>{mainName['name-raw'] || mainName.name}</bdi>,
                    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                    ...translateWatchlistBirthdatesForDisplay(mainName.birthdates),
                  ].reduce(
                    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                    (prev, curr) => (prev.length === 0 ? [curr] : [...prev, ' • ', curr]),
                    [],
                  )}
                </Paragraph>
              }
            >
              {group.map((entity, index) => {
                return (
                  <OtherNameText key={index}>
                    {[
                      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                      translateWatchlistMatchType(entity['alias-type']),
                      entity['name-raw'] || entity.name,
                    ].join(' • ')}
                  </OtherNameText>
                );
              })}
            </Accordion>
          );
        })}
      </>
    );
  } else {
    return (
      <>
        {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
        {entities.map((entity, index) => {
          return (
            <div key={index}>
              {[
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                translateWatchlistMatchType(entity['alias-type']),
                entity['name-raw'] || entity.name,
                ...translateWatchlistBirthdatesForDisplay(entity.birthdates),
              ].join(' • ')}
            </div>
          );
        })}
      </>
    );
  }
};

interface WatchlistProps {
  report: ReportWatchlist | ReportSnapshotWatchlist;
  multiCardFormat: boolean;
  environmentName?: string;
  readOnly?: boolean;
}

const Watchlist = ({
  report,
  multiCardFormat = false,
  environmentName,
  readOnly = false,
}: WatchlistProps) => {
  const [isActiveTabSelected, setIsActiveTabSelected] = useState(true);
  const [ignoreHitsModal, { enable: showIgnoreHitsModal, disable: hideIgnoreHitsModal }] =
    useToggle(false);
  const {
    'matched-lists': matchedLists,
    'entity-lists': entityLists,
    'searched-categories': searchedCategories = [],
    'ignore-list': ignoreList = [],
    'ignore-entity-list': ignoreEntityList = [],
  } = report.attributes;

  if (matchedLists == null) {
    return <></>;
  }

  const groupedMatches = isActiveTabSelected
    ? groupBy('list-type', matchedLists)
    : groupBy(
        'list-type',
        ignoreList.map((entry) => entry.match),
      );
  const listsToDisplay = new Set([...searchedCategories, ...Object.keys(groupedMatches)]);

  const isSnapshot = report.type === ApiModel.ReportSnapshotWatchlist;

  if (!isEmpty(entityLists) || !isEmpty(ignoreEntityList)) {
    const ignoredEntities = ignoreEntityList?.map((entry) => entry.match) ?? [];

    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    if (entityLists.length === 1 && ignoredEntities.length === 0) {
      return (
        <WatchlistEntityMatchDetails
          report={report}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          entity={entityLists[0]}
          multiCardFormat={multiCardFormat}
          readOnly={readOnly}
        />
      );
    }

    return (
      <SentryRoutes>
        {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
        {entityLists.map((entity, idx) => (
          <Route
            path={`entity-matches/active/${idx}`}
            key={idx}
            element={
              <WatchlistEntityMatchDetails
                key={idx}
                report={report}
                entity={entity}
                multiCardFormat={multiCardFormat}
                readOnly={readOnly}
              />
            }
          />
        ))}
        {ignoredEntities.map((entity, idx) => (
          <Route
            path={`entity-matches/dismissed/${idx}`}
            key={idx}
            element={
              <WatchlistEntityMatchDetails
                key={idx}
                report={report}
                entity={entity}
                multiCardFormat={multiCardFormat}
                isIgnoredEntity
                readOnly={readOnly}
              />
            }
          />
        ))}
        <Route
          index
          element={
            <ReportResultLayout
              multiCardFormat={multiCardFormat}
              dataTestKey={`watchlist-${environmentName}`}
            >
              <>
                {multiCardFormat && matchedLists.length > 0 && (
                  <TitleContainer>
                    <Card.Title>Identity Matches</Card.Title>
                  </TitleContainer>
                )}
                {ignoredEntities.length > 0 && (
                  <>
                    <TabBar>
                      <Tab
                        id='report-watchlist__entity-tab-active'
                        selected={isActiveTabSelected}
                        onClick={() => {
                          setIsActiveTabSelected(true);
                        }}
                      >
                        <TabText>
                          <span>Active</span>
                          {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
                          <Sticker>{entityLists.length}</Sticker>
                        </TabText>
                      </Tab>
                      <Tab
                        id='report-watchlist__entity-tab-ignored'
                        selected={!isActiveTabSelected}
                        onClick={() => {
                          setIsActiveTabSelected(false);
                        }}
                      >
                        <TabText>
                          <span>Dismissed</span>
                          <Sticker>{ignoredEntities.length}</Sticker>
                        </TabText>
                      </Tab>
                    </TabBar>
                    <Spacer height={1.5} />
                  </>
                )}
                {isActiveTabSelected ? (
                  <>
                    {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
                    {entityLists.length === 0 ? (
                      <MatchIconPositive label='No matches found.' />
                    ) : (
                      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                      entityLists.map((entity, idx) => (
                        <EntityMatchLink
                          key={idx}
                          to={
                            isSnapshot
                              ? `/dashboard/report-snapshot/${report.id}/entity-matches/active/${idx}`
                              : `/dashboard/reports/${report.id}/entity-matches/active/${idx}`
                          }
                          state={{ internalRedirect: true }}
                        >
                          <WatchlistEntityMatchSummary entity={entity} />
                        </EntityMatchLink>
                      ))
                    )}
                  </>
                ) : (
                  <>
                    {ignoredEntities.map((entity, idx) => (
                      <EntityMatchLink
                        key={idx}
                        to={
                          isSnapshot
                            ? `/dashboard/report-snapshot/${report.id}/entity-matches/dismissed/${idx}`
                            : `/dashboard/reports/${report.id}/entity-matches/dismissed/${idx}`
                        }
                        state={{ internalRedirect: true }}
                      >
                        <WatchlistEntityMatchSummary entity={entity} isDismissed />
                      </EntityMatchLink>
                    ))}
                  </>
                )}
              </>
            </ReportResultLayout>
          }
        />
        <Route path='*' element={<Navigate to='.' replace />} />
      </SentryRoutes>
    );
  }

  const content =
    Object.keys(groupedMatches).length > 0 ? (
      <WatchlistAccordions
        prefixKey={
          isActiveTabSelected ? `active-${matchedLists.length}` : `dismissed-${ignoreList.length}`
        }
        groupedMatches={groupedMatches}
        listOrder={CATEGORY_ORDER.filter((category) => listsToDisplay.has(category))}
        title={translateWatchlistType}
        subtitle={(match: WatchlistMatch) => {
          const isLegacyData = !match['flag-code'];
          return isLegacyData ? (
            <LegacyWatchlistMatchedEntities entities={match.entities} />
          ) : (
            <PersonaWatchlistMatchedEntities entities={match.entities} />
          );
        }}
      />
    ) : (
      <MatchIconPositive label={`No ${isActiveTabSelected ? 'active' : 'dismissed'} matches`} />
    );

  return (
    <ReportResultLayout
      multiCardFormat={multiCardFormat}
      dataTestKey={`watchlist-${environmentName}`}
    >
      <>
        {multiCardFormat && matchedLists.length > 0 && (
          <TitleContainer>
            <Card.Title>Results</Card.Title>

            <Permissioned permission={Permission.ReportIgnoreHitsWrite}>
              <ContextMenu
                variant='inverse'
                items={[
                  {
                    id: 'reports-ignore-hits-modal__dismiss-hits-button',
                    onClick: showIgnoreHitsModal,
                    hidden: false,
                    label: 'Dismiss matches',
                  },
                ]}
                renderItem={(item) => (
                  <ContextMenu.Item key={item.id} id={item.id} onClick={item.onClick}>
                    {item.label}
                  </ContextMenu.Item>
                )}
                disabled={readOnly}
              />
              <IgnoreHitsModal
                report={report}
                ignoreHitsModalOpen={ignoreHitsModal}
                hideIgnoreHitsModal={hideIgnoreHitsModal}
              />
            </Permissioned>
          </TitleContainer>
        )}

        {ignoreList.length > 0 && (
          <>
            <TabBar>
              <Tab
                id='report-watchlist__ignored-tab-active'
                selected={isActiveTabSelected}
                onClick={() => {
                  setIsActiveTabSelected(true);
                }}
              >
                <TabText>
                  <span>Active</span>
                  <Sticker>{matchedLists.length}</Sticker>
                </TabText>
              </Tab>
              <Tab
                id='report-watchlist__ignored-tab-ignored'
                selected={!isActiveTabSelected}
                onClick={() => {
                  setIsActiveTabSelected(false);
                }}
              >
                <TabText>
                  <span>Dismissed</span>
                  <Sticker>{ignoreList.length}</Sticker>
                </TabText>
              </Tab>
            </TabBar>
            <Spacer height={2} />
          </>
        )}
        {content}
      </>
    </ReportResultLayout>
  );
};

export default Watchlist;
